import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ArrowRight, Upload, X, Building2, Package, Users, CreditCard, Check, Eye } from 'lucide-react';
import ListingPreview from '../../components/ListingPreview';
import useDiscount, { DiscountNote } from '../../lib/useDiscount';
import { TermsTick } from '../../components/TermsTick';
import { StaffCountInput, bandForStaff } from '../../components/StaffCountInput';
import { SalesCallCard } from '../../components/SalesCallCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = 'w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#00A79D]/40';

const Field = ({ label, hint, children }) => (
  <div><label className="text-sm font-medium text-gray-700">{label}{hint && <span className="text-gray-400 font-normal"> · {hint}</span>}</label>{children}</div>
);

const Step = ({ n, icon: Icon, title, children, testid }) => (
  <section className="bg-white border border-[#E5E7EB] rounded-xl p-6 space-y-4" data-testid={testid}>
    <h2 className="font-heading text-lg font-bold text-gray-900 flex items-center gap-2">
      <span className="w-7 h-7 rounded-full bg-[#00A79D]/10 text-[#00857D] text-sm flex items-center justify-center">{n}</span>
      <Icon className="w-4 h-4 text-[#00A79D]" /> {title}
    </h2>
    {children}
  </section>
);

const Uploader = ({ kind, max, urls, onChange, testid }) => {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, max - urls.length);
    e.target.value = '';
    if (!files.length) return;
    setBusy(true);
    const out = [...urls];
    for (const f of files) {
      const fd = new FormData(); fd.append('file', f);
      try { const { data } = await axios.post(`${API}/public/create-listing/upload?kind=${kind}`, fd); out.push(data.url); }
      catch (err) { toast.error(err.response?.data?.detail || `Could not upload ${f.name}`); }
    }
    onChange(out); setBusy(false);
  };
  return (
    <div className="flex flex-wrap gap-3 mt-2" data-testid={testid}>
      {urls.map((u) => (
        <div key={u} className="relative w-24 h-24 rounded-lg border border-[#E5E7EB] bg-gray-50 overflow-hidden">
          <img src={u} alt="" className="w-full h-full object-contain" />
          <button type="button" onClick={() => onChange(urls.filter((x) => x !== u))} className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5" data-testid={`${testid}-remove`}><X className="w-3.5 h-3.5 text-gray-600" /></button>
        </div>
      ))}
      {urls.length < max && (
        <label className="w-24 h-24 rounded-lg border-2 border-dashed border-[#E5E7EB] hover:border-[#00A79D] flex flex-col items-center justify-center text-xs text-gray-500 cursor-pointer transition-colors">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Upload className="w-5 h-5 mb-1" />Upload</>}
          <input type="file" accept="image/*" multiple={max > 1} className="hidden" onChange={pick} data-testid={`${testid}-input`} />
        </label>
      )}
    </div>
  );
};

const CategoryPicker = ({ groups, value, onChange }) => {
  const toggle = (c) => {
    if (value.includes(c)) onChange(value.filter((x) => x !== c));
    else if (value.length >= 3) toast.error('Choose up to 3 categories');
    else onChange([...value, c]);
  };
  return (
    <div className="mt-2 max-h-72 overflow-y-auto border border-[#E5E7EB] rounded-lg p-3 space-y-3" data-testid="cl-categories">
      {groups.map((g) => (
        <div key={g.group}>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">{g.group}</p>
          <div className="flex flex-wrap gap-1.5">
            {g.items.map((c) => (
              <button key={c} type="button" onClick={() => toggle(c)} data-testid={`cl-cat-${c.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${value.includes(c) ? 'bg-[#00A79D] border-[#00A79D] text-[#ffffff]' : 'border-[#E5E7EB] text-gray-700 hover:border-[#00A79D]'}`}>
                {value.includes(c) && <Check className="w-3 h-3 inline mr-0.5" />}{c}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const EMPTY = {
  contact_name: '', email: '', job_title: '', phone: '', company_name: '', website: '', companies_house_number: '',
  address: '', tagline: '', description: '', band: '', staff_count: '', product_name: '', product_description: '', categories: [],
  care_types: [], logo: [], screenshots: [], plan: 'monthly', addons: [], editor_emails: '', promo_code: '',
};

export default function CreateListing() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [gate, setGate] = useState(null);
  useEffect(() => { axios.get(`${API}/public/sales-gate`).then((r) => setGate(r.data)).catch(() => {}); }, []);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  useEffect(() => {
    axios.get(`${API}/public/create-listing/meta`, { params: token ? { token } : {} }).then((r) => {
      setMeta(r.data);
      setF((s) => ({ ...s, ...Object.fromEntries(Object.entries(r.data.prefill || {}).filter(([, v]) => v)) }));
    }).catch(() => setMeta(false));
  }, [token]);
  useEffect(() => { if (params.get('cancelled')) toast('Payment cancelled - your details are saved, you can try again.'); }, [params]);

  const bandSel = meta ? bandForStaff(f.staff_count, meta.bands) : null;
  const subSel = bandSel ? (f.plan === 'annual' ? bandSel.annual : bandSel.monthly) : 0;
  const addSel = meta ? f.addons.reduce((t, k) => t + (meta.addons.find((a) => a.key === k)?.price || 0), 0) : 0;
  const disc = useDiscount(f.promo_code, subSel, addSel);
  const [agreed, setAgreed] = useState(false);

  if (meta === false) return <div className="max-w-3xl mx-auto px-4 py-16 text-gray-600">Could not load the form. Please refresh.</div>;
  if (!meta) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

  const band = bandSel;
  const subPrice = subSel;
  const addonTotal = addSel;
  const totalDue = Math.max(0, Math.round((subPrice + addonTotal - disc.discount) * 100) / 100);
  const toggleList = (k, v) => setF((s) => ({ ...s, [k]: s[k].includes(v) ? s[k].filter((x) => x !== v) : [...s[k], v] }));

  const submit = async () => {
    const need = [['contact_name', 'your name'], ['email', 'your email'], ['company_name', 'company name'], ['website', 'website'],
      ['description', 'company description'], ['staff_count', 'how many staff are in your team'], ['product_name', 'product name'], ['product_description', 'product description']];
    const miss = need.find(([k]) => !String(f[k] || '').trim());
    if (miss) { toast.error(`Please add ${miss[1]}`); return; }
    if (!f.categories.length) { toast.error('Choose at least one category'); return; }
    if (!agreed) { toast.error('Please tick to agree to the Terms of Business'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/create-listing`, {
        ...f, token: token || null, logo_url: f.logo[0] || null, logo: undefined,
        band: bandSel?.key || '', staff_count: Number(f.staff_count) || null,
        editor_emails: f.editor_emails.split(',').map((x) => x.trim()).filter((x) => x.includes('@')),
      });
      const co = await axios.post(`${API}/public/claim/${data.claim_id}/checkout`, { terms_accepted: true });
      if (co.data.free && co.data.set_password_token) { window.location.href = `/claim/set-password?token=${co.data.set_password_token}`; return; }
      window.location.href = co.data.url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not continue - please check the form'); setBusy(false); }
  };

  return (
    <div className="bg-[#F4F7F9] min-h-screen" data-testid="create-listing-page">
      <div className="max-w-6xl mx-auto px-4 py-12 lg:grid lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-8 lg:items-start">
      <div className="space-y-6 min-w-0">
        <div>
          <span className="inline-flex text-xs font-semibold text-[#00857D] bg-[#00A79D]/10 px-3 py-1 rounded-full mb-3">New supplier listing</span>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-gray-900 tracking-tight">Create your listing</h1>
          <p className="text-gray-600 mt-3">Not on Care Tech Compare yet? Tell us about your company and product, pick your plan and pay securely. We'll check your listing and publish it, usually within 2 working days. Already listed? <Link to="/browse" className="text-[#00857D] font-semibold">Find and claim it</Link>.</p>
        </div>

        <Step n={1} icon={Users} title="About you" testid="cl-step-you">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Your name"><input value={f.contact_name} onChange={set('contact_name')} className={inputCls} data-testid="cl-contact-name" /></Field>
            <Field label="Work email"><input type="email" value={f.email} onChange={set('email')} className={inputCls} data-testid="cl-email" /></Field>
            <Field label="Job title" hint="optional"><input value={f.job_title} onChange={set('job_title')} className={inputCls} data-testid="cl-job-title" /></Field>
            <Field label="Phone" hint="optional"><input value={f.phone} onChange={set('phone')} className={inputCls} data-testid="cl-phone" /></Field>
          </div>
        </Step>

        <Step n={2} icon={Building2} title="Your company" testid="cl-step-company">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Company name"><input value={f.company_name} onChange={set('company_name')} className={inputCls} data-testid="cl-company-name" /></Field>
            <Field label="Website"><input value={f.website} onChange={set('website')} placeholder="www.yourcompany.co.uk" className={inputCls} data-testid="cl-website" /></Field>
            <Field label="Companies House number" hint="optional"><input value={f.companies_house_number} onChange={set('companies_house_number')} className={inputCls} data-testid="cl-ch-number" /></Field>
            <Field label="Address" hint="optional"><input value={f.address} onChange={set('address')} className={inputCls} data-testid="cl-address" /></Field>
          </div>
          <Field label="Tagline" hint="one line, optional"><input value={f.tagline} onChange={set('tagline')} maxLength={120} className={inputCls} data-testid="cl-tagline" /></Field>
          <Field label="About your company"><textarea value={f.description} onChange={set('description')} rows={4} className={inputCls} data-testid="cl-description" /></Field>
          <Field label="Logo" hint="square works best"><Uploader kind="logo" max={1} urls={f.logo} onChange={set('logo')} testid="cl-logo" /></Field>
          <Field label="How many staff are in your team?" hint="this sets your price">
            <StaffCountInput value={f.staff_count} onChange={set('staff_count')} band={bandSel} testid="cl-staff" className="mt-2" />
          </Field>
        </Step>

        <Step n={3} icon={Package} title="Your product" testid="cl-step-product">
          <Field label="Product name"><input value={f.product_name} onChange={set('product_name')} className={inputCls} data-testid="cl-product-name" /></Field>
          <Field label="What does it do?"><textarea value={f.product_description} onChange={set('product_description')} rows={4} className={inputCls} data-testid="cl-product-description" /></Field>
          <Field label="Categories" hint={`up to 3 · ${f.categories.length}/3 chosen`}><CategoryPicker groups={meta.category_groups} value={f.categories} onChange={set('categories')} /></Field>
          <Field label="Care settings it's built for">
            <div className="flex flex-wrap gap-2 mt-2" data-testid="cl-care-types">
              {meta.care_types.map((c) => (
                <label key={c} className="inline-flex items-center gap-2 text-sm text-gray-700 border border-[#E5E7EB] rounded-full px-3 py-1.5 cursor-pointer">
                  <input type="checkbox" checked={f.care_types.includes(c)} onChange={() => toggleList('care_types', c)} className="accent-[#00A79D]" />{c}
                </label>
              ))}
            </div>
          </Field>
          <Field label="Product screenshots" hint="up to 4"><Uploader kind="product" max={4} urls={f.screenshots} onChange={set('screenshots')} testid="cl-screenshots" /></Field>
        </Step>

        <Step n={4} icon={CreditCard} title="Plan & payment" testid="cl-step-plan">
          {!band ? <p className="text-sm text-gray-500">Choose your team size above to see your price.</p> : (
            <div className="grid sm:grid-cols-2 gap-3">
              {[['monthly', `£${band.monthly}`, 'per month'], ['annual', `£${band.annual}`, 'per year · 1 month free']].map(([p, price, sub]) => (
                <button key={p} type="button" onClick={() => set('plan')(p)} data-testid={`cl-plan-${p}`}
                  className={`text-left border rounded-lg px-4 py-3 transition-colors ${f.plan === p ? 'border-[#00A79D] bg-[#00A79D]/5' : 'border-[#E5E7EB]'}`}>
                  <div className="font-heading font-bold text-gray-900">{price} <span className="text-xs font-normal text-gray-500">{sub}</span></div>
                  <div className="text-xs text-gray-500 capitalize">{p} · {band.label}</div>
                </button>
              ))}
            </div>
          )}
          {meta.addons.length > 0 && <Field label="Optional add-ons" hint="one-off">
            <div className="mt-2 space-y-2">
              {meta.addons.map((a) => (
                <label key={a.key} className="flex items-center gap-3 border border-[#E5E7EB] rounded-lg px-4 py-2.5 cursor-pointer" data-testid={`cl-addon-${a.key}`}>
                  <input type="checkbox" checked={f.addons.includes(a.key)} onChange={() => toggleList('addons', a.key)} className="accent-[#00A79D] w-4 h-4" />
                  <span className="text-sm text-gray-700 flex-1">{a.label}</span><span className="text-sm font-semibold text-gray-900">£{a.price}</span>
                </label>
              ))}
            </div>
          </Field>}
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Team access" hint="optional"><input value={f.editor_emails} onChange={set('editor_emails')} placeholder="colleague@company.com" className={inputCls} data-testid="cl-editor-emails" /></Field>
            <Field label="Discount code" hint="optional"><input value={f.promo_code} onChange={set('promo_code')} className={`${inputCls} uppercase`} data-testid="cl-promo-code" /><DiscountNote d={disc} testid="cl-discount" /></Field>
          </div>
          {gate?.gated_bands?.includes(bandSel?.key) ? <SalesCallCard gate={gate} company={f.company_name} email={f.email} name={f.contact_name} staffCount={Number(f.staff_count) || null} band={bandSel?.key} source="create_listing_large" testid="cl-sales-call" /> : (
          <div className="border-t border-[#E5E7EB] pt-4 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-sm text-gray-500">Total due today</div>
              <div className="font-heading text-2xl font-extrabold text-gray-900" data-testid="cl-total">£{totalDue}</div>
              {band && <div className="text-xs text-gray-500 mt-0.5" data-testid="cl-breakdown">Then £{subPrice}/{f.plan === 'annual' ? 'yr' : 'mo'}{addonTotal > 0 ? ` · £${addonTotal} add-ons charged once` : ''}{disc.discount > 0 ? ` · includes £${disc.discount} discount on your first payment` : ''}</div>}
            </div>
          <TermsTick checked={agreed} onChange={setAgreed} testid="cl-terms" />
            <button type="button" onClick={submit} disabled={busy} data-testid="cl-submit"
              className="inline-flex items-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-[#ffffff] font-semibold px-6 py-3 rounded-md disabled:opacity-60 transition-colors">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue to secure payment <ArrowRight className="w-4 h-4" /></>}
            </button>
          </div>)}
        </Step>
      </div>
      <aside className="hidden lg:block sticky top-24"><ListingPreview f={f} /></aside>
      </div>
      <button type="button" onClick={() => setShowPreview(true)} data-testid="cl-preview-mobile-btn"
        className="lg:hidden fixed bottom-24 right-4 z-40 inline-flex items-center gap-1.5 bg-[#1A3A5F] text-[#ffffff] text-sm font-semibold px-4 py-2.5 rounded-full shadow-lg">
        <Eye className="w-4 h-4" /> Preview
      </button>
      {showPreview && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/50 flex items-end" onClick={() => setShowPreview(false)} data-testid="cl-preview-mobile">
          <div className="bg-[#F4F7F9] w-full max-h-[85vh] overflow-y-auto rounded-t-2xl p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-end mb-2"><button onClick={() => setShowPreview(false)} data-testid="cl-preview-close"><X className="w-5 h-5 text-gray-500" /></button></div>
            <ListingPreview f={f} />
          </div>
        </div>
      )}
    </div>
  );
}
